import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

export const THEME_STORAGE_KEY = 'tas_theme_mode';

export const ThemeModeContext = createContext({
  preference: 'system',
  resolvedMode: 'light',
  setPreference: () => {},
  toggleTheme: () => {},
});

export const ThemeModeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // localStorage may be disabled or restricted
    }
    return 'system';
  });

  const [systemIsDark, setSystemIsDark] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setSystemIsDark(e.matches);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const setPreference = (newPref) => {
    if (newPref !== 'light' && newPref !== 'dark' && newPref !== 'system') return;
    setPreferenceState(newPref);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newPref);
    } catch {
      // ignore
    }
  };

  const resolvedMode = useMemo(() => {
    if (preference === 'system') {
      return systemIsDark ? 'dark' : 'light';
    }
    return preference;
  }, [preference, systemIsDark]);

  const toggleTheme = useCallback(() => {
    const next = resolvedMode === 'dark' ? 'light' : 'dark';
    setPreference(next);
  }, [resolvedMode]);

  const contextValue = useMemo(() => ({
    preference,
    resolvedMode,
    setPreference,
    toggleTheme,
  }), [preference, resolvedMode, toggleTheme]);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export default ThemeModeContext;